import React, { useCallback, useMemo, useState } from 'react';
import {
  Modal, Tabs, Form, Input, Upload, Select, Alert, Badge, Table, Tag, Spin, Button, Empty,
} from 'antd';
import { InboxOutlined, FileExcelOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { XLSX } from '../../../utils/xlsxShim';
import bilateralesService from '../../../services/bilateralesService';
import { filtreSansAccents } from '../../../utils/rechercheTexte';
import { useTranslation, Trans } from 'react-i18next';

const { Dragger } = Upload;

/**
 * Import de dossiers dans une session compagnie (mode RECEPTION).
 *
 * Deux entrees, un seul endpoint. `POST /v1/sessions-compagnie/{id}/import-dossiers`
 * n'accepte qu'une liste de NUMEROS et les cherche par numero local, homologue ou
 * assureur. Le fichier Excel est donc lu ICI, dans le navigateur, et reduit a cette
 * meme liste : aucun endpoint supplementaire, aucun fichier a televerser, et le
 * rapport d'import (importes / doublons / erreurs) reste celui du backend.
 *
 * Le collage manuel est conserve a cote : c'est le mode qui fonctionne quand on a
 * trois numeros sous les yeux, et le retirer ferait regresser un usage existant.
 *
 * <h3>Pourquoi un choix de colonne explicite</h3>
 * Les fichiers viennent des homologues et ne sont pas normalises : selon le pays et
 * l'annee, la colonne utile s'appelle « SINISTRE BNCB TOGO », « N° SINISTRE », ou
 * rien du tout. L'auto-detection couvre les en-tetes connus du format metier
 * (cf. l'export bilateral : colonnes C, D, E), mais reste une PROPOSITION que
 * l'utilisateur voit et peut corriger, avec l'apercu de ce qui partira vraiment.
 * Deviner en silence sur un fichier inconnu importerait des colonnes de montants.
 */

/* En-tetes connus du format metier, par ordre de preference. Le backend cherchant
   indifferemment sur les trois, on privilegie le numero local qui est le plus sur. */
const ENTETES_CONNUS = [
  'sinistre bncb togo',
  'sinistre bncb',
  'n° sinistre',
  'no sinistre',
  'numero sinistre',
  'numéro sinistre',
  'sin assureur togo',
  'sinistre assureur',
];

const normaliser = (v) => String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Une cellule exploitable comme numero : ni vide, ni un nombre pur (montant, rang). */
const estNumeroPlausible = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return false;
  // Un entier seul est presque toujours un montant ou un n° d'ordre, jamais une
  // reference de sinistre (« DR8 394/2019 », « 115/2019/200072 »).
  if (/^\d+([.,]\d+)?$/.test(s)) return false;
  return true;
};

/**
 * Cherche la ligne d'en-tetes : la premiere des 15 premieres lignes qui contient un
 * en-tete connu. Les fichiers metier ont un titre fusionne et un bandeau statistique
 * avant le tableau - commencer a la ligne 1 ramenerait ce bandeau.
 */
const trouverEnTetes = (lignes) => {
  const limite = Math.min(lignes.length, 15);
  for (let i = 0; i < limite; i += 1) {
    const cellules = (lignes[i] || []).map(normaliser);
    if (cellules.some((c) => c && ENTETES_CONNUS.includes(c))) return i;
  }
  return -1;
};

const colonneParDefaut = (entetes) => {
  for (const attendu of ENTETES_CONNUS) {
    const idx = entetes.findIndex((e) => normaliser(e) === attendu);
    if (idx >= 0) return idx;
  }
  return -1;
};

/**
 * Modele vierge a remplir, telechargeable depuis la modale.
 *
 * Deux feuilles, et l'ordre compte : « Dossiers » d'abord, VIDE sous ses en-tetes.
 * C'est elle que l'auto-detection retiendra au moment de la relecture. Les lignes
 * d'exemple vivent sur une feuille separee - laissees dans la feuille de saisie,
 * elles seraient importees telles quelles par quiconque oublie de les effacer, et
 * reviendraient en « non trouve » dans le rapport.
 *
 * Les trois colonnes de numeros correspondent aux trois references sur lesquelles le
 * backend sait chercher. Il suffit d'en remplir UNE : c'est le selecteur de colonne
 * de la modale qui decide laquelle est lue. Aucune colonne de montant n'est proposee,
 * l'import ne cree que le rattachement du dossier a la session.
 */
const ENTETES_MODELE = [
  "N° d'ordre",
  'SINISTRE BNCB TOGO',
  'SINISTRE BNCB HOMOLOGUE',
  'SIN ASSUREUR TOGO',
];

const telechargerModele = async () => {
  const wb = XLSX.utils.book_new();

  const saisie = XLSX.utils.aoa_to_sheet([
    ["Modèle d'import - rencontres bilatérales CBConnect"],
    ['Remplissez UNE des trois colonnes de numéros. Une ligne par dossier. Ne modifiez pas la ligne d’en-têtes.'],
    [],
    ENTETES_MODELE,
  ]);
  saisie['!cols'] = [{ wch: 10 }, { wch: 26 }, { wch: 26 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(wb, saisie, 'Dossiers');

  const exemple = XLSX.utils.aoa_to_sheet([
    ['Exemples - à titre indicatif, cette feuille n’est pas importée'],
    [],
    ENTETES_MODELE,
    [1, 'DR8 394/2019', 'BF/2019/0412', '115/2019/200072'],
    [2, 'DR8 401/2019', '', ''],
    [3, '', 'BF/2020/0088', ''],
  ]);
  exemple['!cols'] = [{ wch: 10 }, { wch: 26 }, { wch: 26 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(wb, exemple, 'Exemple');

  await XLSX.writeFile(wb, 'modele-import-dossiers-bilateraux.xlsx');
};

export default function ImportDossiersModal({ open, sessionTrackingId, onClose, onImported }) {
  const { t } = useTranslation('bilaterales');
  const [ongletActif, setOngletActif] = useState('excel');
  const [form] = Form.useForm();
  const [resultat, setResultat] = useState(null);
  const [enCours, setEnCours] = useState(false);

  /* ── Etat du fichier Excel ── */
  const [nomFichier, setNomFichier] = useState(null);
  const [analyse, setAnalyse] = useState(false);
  const [feuilles, setFeuilles] = useState([]);        // [{ nom, lignes: [][] }]
  const [feuilleActive, setFeuilleActive] = useState(null);
  const [ligneEntetes, setLigneEntetes] = useState(-1);
  const [colonne, setColonne] = useState(-1);

  const reinitialiserFichier = () => {
    setNomFichier(null);
    setFeuilles([]);
    setFeuilleActive(null);
    setLigneEntetes(-1);
    setColonne(-1);
  };

  const fermer = () => {
    reinitialiserFichier();
    setResultat(null);
    form.resetFields();
    onClose?.();
  };

  const feuille = useMemo(
    () => feuilles.find((f) => f.nom === feuilleActive) || null,
    [feuilles, feuilleActive],
  );

  const entetes = useMemo(() => {
    if (!feuille || ligneEntetes < 0) return [];
    return feuille.lignes[ligneEntetes] || [];
  }, [feuille, ligneEntetes]);

  /** Numeros extraits de la colonne choisie, dedoublonnes en preservant l'ordre. */
  const numerosExtraits = useMemo(() => {
    if (!feuille || colonne < 0) return [];
    const debut = ligneEntetes >= 0 ? ligneEntetes + 1 : 0;
    const vus = new Set();
    const sortie = [];
    for (let i = debut; i < feuille.lignes.length; i += 1) {
      const brut = feuille.lignes[i]?.[colonne];
      if (!estNumeroPlausible(brut)) continue;
      const valeur = String(brut).trim();
      const cle = valeur.toLowerCase();
      if (vus.has(cle)) continue;
      vus.add(cle);
      sortie.push(valeur);
    }
    return sortie;
  }, [feuille, colonne, ligneEntetes]);

  /* Le doublon interne au fichier est retire ici et signale : le backend, lui, ne
     compte comme doublon que ce qui est deja dans la session. Ne pas les confondre. */
  const doublonsFichier = useMemo(() => {
    if (!feuille || colonne < 0) return 0;
    const debut = ligneEntetes >= 0 ? ligneEntetes + 1 : 0;
    let total = 0;
    for (let i = debut; i < feuille.lignes.length; i += 1) {
      if (estNumeroPlausible(feuille.lignes[i]?.[colonne])) total += 1;
    }
    return total - numerosExtraits.length;
  }, [feuille, colonne, ligneEntetes, numerosExtraits.length]);

  const analyserFichier = useCallback((file) => {
    setNomFichier(file.name);
    setAnalyse(true);
    setResultat(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      // Parsing defere : laisse React peindre l'etat « analyse » avant de bloquer
      // le thread principal sur un classeur de plusieurs milliers de lignes.
      setTimeout(async () => {
        try {
          const wb = await XLSX.read(e.target.result, { type: 'array', cellDates: true });
          const lues = wb.SheetNames.map((nom) => ({
            nom,
            lignes: XLSX.utils.sheet_to_json(wb.Sheets[nom], { header: 1, defval: null }),
          })).filter((f) => f.lignes.length > 0);

          if (!lues.length) {
            toast.error(t('import.aucuneFeuille'));
            reinitialiserFichier();
            return;
          }

          setFeuilles(lues);
          // Premiere feuille ou l'on reconnait le format metier, sinon la premiere.
          const reconnue = lues.find((f) => trouverEnTetes(f.lignes) >= 0) || lues[0];
          setFeuilleActive(reconnue.nom);
          const idxEntetes = trouverEnTetes(reconnue.lignes);
          setLigneEntetes(idxEntetes);
          setColonne(idxEntetes >= 0 ? colonneParDefaut(reconnue.lignes[idxEntetes]) : -1);
        } catch (err) {
          toast.error(t('import.fichierIllisible'));
          reinitialiserFichier();
        } finally {
          setAnalyse(false);
        }
      }, 0);
    };
    reader.onerror = () => {
      toast.error(t('import.lectureImpossible'));
      reinitialiserFichier();
      setAnalyse(false);
    };
    reader.readAsArrayBuffer(file);

    return Upload.LIST_IGNORE; // on gere l'etat nous-memes, pas de liste antd
  }, [t]);

  const changerFeuille = (nom) => {
    setFeuilleActive(nom);
    const f = feuilles.find((x) => x.nom === nom);
    const idx = f ? trouverEnTetes(f.lignes) : -1;
    setLigneEntetes(idx);
    setColonne(idx >= 0 ? colonneParDefaut(f.lignes[idx]) : -1);
  };

  const envoyer = async (numeros) => {
    if (!numeros.length) {
      toast.error(t('import.aucunNumero'));
      return;
    }
    setEnCours(true);
    try {
      const data = await bilateralesService.importerNumeros(sessionTrackingId, numeros);
      setResultat(data);
      toast.success(t('import.termine', { importes: data.importes, total: data.totalFournis }));
      onImported?.();
    } catch (_) {
      // toast deja emis par l'intercepteur axios
    } finally {
      setEnCours(false);
    }
  };

  const importerDepuisTexte = async (vals) => {
    const numeros = (vals.numeros || '').split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
    await envoyer(numeros);
  };

  const optionsColonnes = useMemo(() => {
    if (!feuille) return [];
    // Sans ligne d'en-tetes reconnue, on propose les colonnes par leur lettre et un
    // echantillon de contenu : c'est le seul repere exploitable.
    const largeur = feuille.lignes.reduce((n, l) => Math.max(n, l?.length || 0), 0);
    const lettre = (i) => {
      let s = ''; let n = i;
      do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
      return s;
    };
    const debut = ligneEntetes >= 0 ? ligneEntetes + 1 : 0;
    return Array.from({ length: largeur }, (_, i) => {
      const titre = ligneEntetes >= 0 ? String(entetes[i] ?? '').trim() : '';
      const exemple = feuille.lignes.slice(debut, debut + 40)
        .map((l) => l?.[i]).find((v) => estNumeroPlausible(v));
      return {
        value: i,
        label: `${lettre(i)} · ${titre || t('import.sansEntete')}${exemple ? ` - ${t('import.exemple', { exemple })}` : ''}`,
      };
    });
  }, [feuille, entetes, ligneEntetes, t]);

  const Rapport = () => (resultat ? (
    <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 12 }}>
      <div><Badge status="success" /> <Trans t={t} i18nKey="import.rapport.importes" values={{ importes: resultat.importes, total: resultat.totalFournis }} components={{ 1: <strong /> }} /></div>
      <div><Badge status="warning" /> <Trans t={t} i18nKey="import.rapport.dejaDansSession" values={{ n: resultat.doublonsIgnores }} components={{ 1: <strong /> }} /></div>
      <div><Badge status="error" /> <Trans t={t} i18nKey="import.rapport.nonTrouves" values={{ n: resultat.erreurs?.length || 0 }} components={{ 1: <strong /> }} /></div>
      {resultat.erreurs?.length > 0 && (
        <ul style={{ maxHeight: 140, overflow: 'auto', margin: '8px 0 0', paddingLeft: 20 }}>
          {resultat.erreurs.map((e, i) => (
            <li key={i} style={{ fontSize: 12.5 }}><code>{e.numeroFourni}</code> - {e.motif}</li>
          ))}
        </ul>
      )}
    </div>
  ) : null);

  return (
    <Modal
      open={open}
      title={t('import.titre')}
      onCancel={fermer}
      width={760}
      destroyOnClose
      footer={
        ongletActif === 'excel'
          ? [
            <Button key="fermer" onClick={fermer}>{t('actions.fermer')}</Button>,
            <Button
              key="import"
              type="primary"
              loading={enCours}
              disabled={!numerosExtraits.length}
              onClick={() => envoyer(numerosExtraits)}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            >
              {t('import.importerDossiers', { count: numerosExtraits.length, nombre: numerosExtraits.length || '' })}
            </Button>,
          ]
          : [
            <Button key="fermer" onClick={fermer}>{t('actions.fermer')}</Button>,
            <Button
              key="import"
              type="primary"
              loading={enCours}
              onClick={() => form.submit()}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            >
              {t('import.importer')}
            </Button>,
          ]
      }
    >
      <Tabs
        activeKey={ongletActif}
        onChange={setOngletActif}
        items={[
          {
            key: 'excel',
            label: <span><FileExcelOutlined /> {t('import.onglets.excel')}</span>,
            children: (
              <div>
                {!nomFichier && !analyse && (
                  <Dragger
                    accept=".xlsx,.xls"
                    multiple={false}
                    beforeUpload={analyserFichier}
                    showUploadList={false}
                    style={{ borderColor: '#1a6b3c', borderRadius: 12 }}
                  >
                    <p style={{ margin: '8px 0' }}><InboxOutlined style={{ fontSize: 40, color: '#1a6b3c' }} /></p>
                    <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                      {t('import.glisserDeposer')}
                    </p>
                    <p style={{ color: '#64748b', fontSize: 12.5, margin: '4px 0 8px' }}>
                      {t('import.formatsAcceptes')}
                    </p>
                  </Dragger>
                )}

                {!nomFichier && !analyse && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    marginTop: 12, padding: '10px 12px', background: '#f8fafc',
                    border: '1px solid #e2e8f0', borderRadius: 8,
                  }}>
                    <span style={{ fontSize: 12.5, color: '#475569' }}>
                      {t('import.pasDeFichier')}
                    </span>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={async () => {
                        try {
                          await telechargerModele();
                        } catch (_) {
                          toast.error(t('import.modeleImpossible'));
                        }
                      }}
                    >
                      {t('import.telechargerModele')}
                    </Button>
                    <span style={{ fontSize: 11.5, color: '#94a3b8', flexBasis: '100%' }}>
                      {t('import.etatRecuAussi')}
                    </span>
                  </div>
                )}

                {analyse && (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin /> <div style={{ marginTop: 10, color: '#64748b' }}>{t('import.analyse')}</div>
                  </div>
                )}

                {nomFichier && !analyse && feuille && (
                  <>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                      padding: '8px 12px', background: '#f0fdf4', borderRadius: 8,
                    }}>
                      <FileExcelOutlined style={{ color: '#1a6b3c' }} />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{nomFichier}</span>
                      <Button size="small" type="text" icon={<ReloadOutlined />}
                        onClick={reinitialiserFichier} style={{ marginLeft: 'auto' }}>
                        {t('import.changer')}
                      </Button>
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{t('import.feuille')}</div>
                        <Select
                          style={{ width: '100%' }}
                          value={feuilleActive}
                          onChange={changerFeuille}
                          options={feuilles.map((f) => ({
                            value: f.nom,
                            label: t('import.feuilleOption', { nom: f.nom, n: f.lignes.length }),
                          }))}
                        />
                      </div>
                      <div style={{ flex: '2 1 300px' }}>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                          {t('import.colonne')}
                        </div>
                        <Select
                          style={{ width: '100%' }}
                          value={colonne >= 0 ? colonne : undefined}
                          onChange={setColonne}
                          placeholder={t('import.choisirColonne')}
                          options={optionsColonnes}
                          showSearch
                          filterOption={filtreSansAccents}
                          optionFilterProp="label"
                        />
                      </div>
                    </div>

                    {ligneEntetes < 0 && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={t('import.entetesNonReconnus')}
                      />
                    )}

                    {colonne < 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('import.choisissezColonne')}
                      />
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                          <Tag color="green" style={{ borderRadius: 12 }}>
                            {t('import.numerosAImporter', { count: numerosExtraits.length })}
                          </Tag>
                          {doublonsFichier > 0 && (
                            <Tag color="gold" style={{ borderRadius: 12 }}>
                              {t('import.doublonsFichier', { count: doublonsFichier })}
                            </Tag>
                          )}
                        </div>

                        <Table
                          size="small"
                          rowKey={(r) => r.numero}
                          dataSource={numerosExtraits.map((numero, i) => ({ numero, rang: i + 1 }))}
                          columns={[
                            { title: '#', dataIndex: 'rang', width: 56, align: 'center' },
                            { title: t('import.numeroRecherche'), dataIndex: 'numero' },
                          ]}
                          pagination={{ pageSize: 8, size: 'small', showSizeChanger: false }}
                          locale={{ emptyText: t('import.aucunNumeroExploitable') }}
                        />
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>
                          {t('import.explicationRecherche')}
                        </div>
                      </>
                    )}
                  </>
                )}

                <Rapport />
              </div>
            ),
          },
          {
            key: 'liste',
            label: t('import.onglets.liste'),
            children: (
              <div>
                <p style={{ color: '#64748b', marginTop: 0, fontSize: 13 }}>
                  {t('import.unNumeroParLigne')}
                </p>
                <Form form={form} layout="vertical" onFinish={importerDepuisTexte}>
                  <Form.Item name="numeros" rules={[{ required: true, message: t('import.saisirNumero') }]}>
                    <Input.TextArea rows={8} placeholder="DR8 394/2019&#10;115/2019/200072&#10;..." />
                  </Form.Item>
                </Form>
                <Rapport />
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}
